import { createContext, useContext, useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) { setLoading(false); return; }

        fetch(API + "/auth/me", { headers: { Authorization: "Bearer " + token } })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data) {
                    setUser({ username: data.username, token, role: data.role, permissions: data.permissions || [] });
                } else {
                    localStorage.removeItem("token");
                }
            })
            .catch(() => { localStorage.removeItem("token"); })
            .finally(() => setLoading(false));
    }, []);

    const login = async (username, password) => {
        const res = await fetch(API + "/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.detail || "Login fehlgeschlagen");
        }
        const data = await res.json();
        localStorage.setItem("token", data.access_token);
        localStorage.removeItem("username");
        localStorage.removeItem("role");
        localStorage.removeItem("permissions");
        setUser({ username, token: data.access_token, role: data.role, permissions: data.permissions || [] });
        return data;
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        localStorage.removeItem("role");
        localStorage.removeItem("permissions");
        setUser(null);
    };

    const hasPermission = (key) => {
        if (!user) return false;
        if (user.role === "admin") return true;
        if (user.permissions?.includes("admin")) return true;
        return user.permissions?.includes(key);
    };

    useEffect(() => {
        if (!user) return;
        const TIMEOUT = 2 * 60 * 60 * 1000;
        let timer;
        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                localStorage.removeItem("token");
                window.location.href = "/login";
            }, TIMEOUT);
        };
        const events = ["mousemove", "keydown", "click", "touchstart"];
        events.forEach(e => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, reset)); };
    }, [user]); // eslint-disable-line

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, hasPermission }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
